import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { keyBy } from 'lodash';
import {
  type TTEUser,
  type TUserProfile,
  userProfilePropertiesArr,
} from '@timeedit/types';
import {
  ConfirmationPopup,
  Icon,
  TEDrawerSection,
  TEInput,
} from '@timeedit/ui-components';
import { Tabs } from '@timeedit/ui-components/antd';
import { PermissionsAndOrgs } from './PermissionsAndOrgs/PermissionsAndOrgs';
import { UserObjects } from './UserObjects';
import './UserProfile.scss';

type UserProfileProps = {
  user: TTEUser;
  onChange: (key: string, value: any) => void;
};

const createDefaultProfile = (user: TTEUser): TUserProfile =>
  userProfilePropertiesArr.reduce<TUserProfile>(
    (acc, prop) => ({ ...acc, [prop]: user[prop] }),
    {} as TUserProfile
  );

const UserProfile = ({ user, onChange }: UserProfileProps) => {
  const [activeKey, setActiveKey] = useState(user.id);

  useEffect(() => {
    setActiveKey(user.id);
  }, [user.id]);

  const defaultProfile: TUserProfile = useMemo(
    () => createDefaultProfile(user),
    [user]
  );

  const allProfiles = useMemo(
    () => [defaultProfile, ...(user.profiles || [])],
    [defaultProfile, user.profiles]
  );

  const profileById = useMemo(() => keyBy(allProfiles, 'id'), [allProfiles]);

  const activeProfile = useMemo(
    () => profileById[activeKey],
    [activeKey, profileById]
  );

  const onDeleteProfile = useCallback(
    (profileId: string) => {
      onChange(
        'profiles',
        user.profiles?.filter((p) => p.id !== profileId)
      );
      setActiveKey(defaultProfile.id);
    },
    [defaultProfile.id, onChange, user.profiles]
  );

  const tabItems = useMemo(
    () =>
      allProfiles.map((profile) => ({
        key: profile.id,
        label: profile.profileName,
        closable: profile.id !== defaultProfile.id,
        closeIcon: profile.id.includes('new-profile-') ? (
          <Icon
            id="CloseOutlined"
            alt="Close"
            onClick={() => onDeleteProfile(profile.id)}
          />
        ) : (
          <ConfirmationPopup
            message="Delete this profile? Deleting this profile will remove all historic data associated with it and cannot be undone. Are you sure?"
            onConfirm={() => onDeleteProfile(profile.id)}
            icon={<Icon alt="Warning" id="Warning" />}
            okText="Delete"
            cancelText="Cancel"
          >
            <Icon id="CloseOutlined" alt="Close" />
          </ConfirmationPopup>
        ),
      })),
    [allProfiles, defaultProfile, onDeleteProfile]
  );

  const isDefaultProfile = useMemo(() => {
    return activeProfile?.id === defaultProfile.id;
  }, [activeProfile?.id, defaultProfile?.id]);

  const onProfileChange = useCallback(
    (key: string, value: any) => {
      if (isDefaultProfile) {
        onChange(key, value);
      } else {
        onChange(
          'profiles',
          user.profiles?.map((p) =>
            p.id === activeProfile?.id ? { ...activeProfile, [key]: value } : p
          )
        );
      }
    },
    [activeProfile, isDefaultProfile, onChange, user.profiles]
  );

  return (
    <TEDrawerSection label="Profiles">
      <Tabs
        className="user-profile-tab"
        defaultActiveKey={defaultProfile.id}
        type="editable-card"
        items={tabItems}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        onEdit={(key, action) => {
          if (action === 'add') {
            const newProfileId = `new-profile-${Date.now()}`;
            onChange('profiles', [
              ...(user.profiles || []),
              {
                id: newProfileId,
                profileName: 'New Profile',
                organizationsAndRoles: [],
                scopes: [],
                appPermissions: [],
                userObjects: [],
              },
            ]);
            setActiveKey(newProfileId);
          }
        }}
      />
      <div className="user-profile-container">
        <div className="user-profile-row">
          <TEInput.Input
            label="Profile name"
            value={activeProfile?.profileName}
            onChange={(val) => onProfileChange('profileName', val)}
          />
        </div>
        <div className="user-profile-row">
          <TEInput.TextArea
            label="Profile description"
            tooltip={
              isDefaultProfile
                ? 'This description is the same as the user description above and cannot be edited here.'
                : undefined
            }
            value={activeProfile?.description}
            disabled={isDefaultProfile}
            onChange={(val) => onProfileChange('description', val)}
          />
        </div>
        <div className="user-profile-row">
          <TEInput.Input
            value={activeProfile?.contactEmail}
            label="Contact email"
            onChange={(val) => onProfileChange('contactEmail', val)}
            tooltip="Adding a contact email address will make ALL Core and Viewer emails be sent from your contact email. If left empty, your regular email will be used as usual."
          />
        </div>
        <div className="user-profile-row">
          <TEInput.Switch
            label="Active"
            checked={activeProfile?.inactiveSince === null}
            onChange={(e) =>
              onProfileChange('inactiveSince', e ? null : dayjs().unix())
            }
            tooltip="Active"
          />
        </div>
        <PermissionsAndOrgs
          profile={activeProfile}
          onChange={onProfileChange}
        />
        <UserObjects profile={activeProfile} onChange={onProfileChange} />
      </div>
    </TEDrawerSection>
  );
};

export default UserProfile;
